import React, { useRef, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Eraser, Camera, PenLine } from "lucide-react";
import { formatGs } from "../lib/format";

function SignaturePad({ inputRef }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0A1F44";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (canvasRef.current.width / rect.width), y: (t.clientY - rect.top) * (canvasRef.current.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
    inputRef.current = canvasRef.current.toDataURL("image/png");
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    inputRef.current = null;
  };

  return (
    <div>
      <canvas
        ref={canvasRef} width={520} height={180} data-testid="signature-canvas"
        className="w-full h-40 rounded-lg border border-slate-300 bg-white touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <button type="button" onClick={clear} className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700">
        <Eraser className="h-3.5 w-3.5" /> Limpiar firma
      </button>
    </div>
  );
}

export function ComprobanteModal({ open, envio, onClose, onConfirm }) {
  const firmaRef = useRef(null);
  const [receptor, setReceptor] = useState("");
  const [foto, setFoto] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) { firmaRef.current = null; setReceptor(""); setFoto(null); } }, [open]);

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const max = 800;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = img.width * scale; c.height = img.height * scale;
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        setFoto(c.toDataURL("image/jpeg", 0.6));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const confirm = async () => {
    setLoading(true);
    try { await onConfirm({ receptor: receptor || envio?.destinatario, firma: firmaRef.current, foto }); }
    finally { setLoading(false); }
  };

  const cobro = envio?.cobro_destinatario || 0;
  const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Comprobante de entrega</DialogTitle>
          <DialogDescription>Confirmá la entrega de {envio?.code} para {envio?.destinatario}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {cobro > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-center justify-between">
              <span className="text-sm font-medium text-amber-800">A cobrar en la entrega</span>
              <span className="font-bold text-[#0A1F44]">{formatGs(cobro)}</span>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-[#64748B]">Recibido por</label>
            <input className={input} value={receptor} onChange={(e) => setReceptor(e.target.value)} placeholder={envio?.destinatario} data-testid="comprobante-receptor-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#64748B] flex items-center gap-1.5"><PenLine className="h-3.5 w-3.5" /> Firma</label>
            <SignaturePad inputRef={firmaRef} />
          </div>
          <div>
            <label className="flex items-center justify-center gap-2 text-sm font-medium text-[#0A1F44] border border-dashed border-slate-300 rounded-lg py-3 cursor-pointer hover:bg-slate-50" data-testid="comprobante-foto-label">
              <Camera className="h-4 w-4" /> {foto ? "Foto adjunta ✓ (cambiar)" : "Adjuntar foto (opcional)"}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="comprobante-foto-input" />
            </label>
            {foto && <img src={foto} alt="comprobante" className="mt-2 w-full max-h-40 object-contain rounded-lg border border-slate-200" />}
          </div>
          <button onClick={confirm} disabled={loading} data-testid="comprobante-confirm-btn"
            className="w-full rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-2.5 text-sm disabled:opacity-60">
            {loading ? "Registrando..." : "Confirmar entrega"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
